import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiSearch, FiSliders, FiX } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const LABELS = {
  en: {
    q: 'Job title, keyword or company', filters: 'Filters', search: 'Search', reset: 'Clear filters',
    location: 'City or country', company: 'Company', work_mode: 'Work mode', job_type: 'Job type',
    seniority: 'Experience level', industry: 'Industry', skill: 'Skill', language: 'Job description language',
    visa: 'Visa sponsorship stated in the job', source: 'Job source', currency: 'Salary currency',
    salary_period: 'Pay period', minimum_salary: 'Minimum salary', posted_days: 'Date posted',
    salary_only: 'Only jobs with a salary listed', direct_employer: 'Only confirmed direct employers',
    all: 'Any', anytime: 'Any time', day: 'Past 24 hours', week: 'Past 7 days', month: 'Past 30 days',
    salaryHint: 'Choose a currency and pay period first.', apply: 'Apply filters', close: 'Close filters', role: 'Role & location', experience: 'Experience & skills', pay: 'Compensation', preferences: 'Other preferences',
  },
  ar: {
    q: 'المسمّى الوظيفي أو كلمة أو اسم شركة', filters: 'خيارات البحث', search: 'بحث', reset: 'مسح الخيارات',
    location: 'المدينة أو البلد', company: 'الشركة', work_mode: 'نمط العمل', job_type: 'نوع الوظيفة',
    seniority: 'مستوى الخبرة', industry: 'القطاع', skill: 'المهارة', language: 'لغة وصف الوظيفة',
    visa: 'رعاية التأشيرة المذكورة في الوظيفة', source: 'مصدر الوظيفة', currency: 'عملة الراتب',
    salary_period: 'فترة الراتب', minimum_salary: 'الحد الأدنى للراتب', posted_days: 'تاريخ النشر',
    salary_only: 'وظائف براتب معلن فقط', direct_employer: 'جهات توظيف مباشرة مؤكّدة فقط',
    all: 'الكل', anytime: 'أي وقت', day: 'آخر ٢٤ ساعة', week: 'آخر ٧ أيام', month: 'آخر ٣٠ يوماً',
    salaryHint: 'اختر عملة الراتب وفترته أولاً.', apply: 'تطبيق الخيارات', close: 'إغلاق الخيارات', role: 'الوظيفة والموقع', experience: 'الخبرة والمهارات', pay: 'الراتب', preferences: 'تفضيلات أخرى',
  },
};
const VALUES = {
  agriculture_food: ['Agriculture and food', 'الزراعة والغذاء'], construction: ['Construction', 'البناء'],
  education: ['Education', 'التعليم'], energy_natural_resources: ['Energy and natural resources', 'الطاقة والموارد الطبيعية'],
  financial_services: ['Financial services', 'الخدمات المالية'], government_public: ['Government', 'القطاع الحكومي'],
  healthcare: ['Healthcare', 'الرعاية الصحية'], hospitality_events: ['Hospitality and events', 'الضيافة والفعاليات'],
  humanitarian_aid: ['Humanitarian aid', 'المساعدات الإنسانية'], logistics_supply_chain: ['Logistics and supply chain', 'الخدمات اللوجستية وسلاسل الإمداد'],
  manufacturing: ['Manufacturing', 'التصنيع'], media_marketing: ['Media and marketing', 'الإعلام والتسويق'],
  nonprofit_social_impact: ['Nonprofits', 'المنظمات غير الربحية'], professional_services: ['Professional services', 'الخدمات المهنية'],
  real_estate: ['Real estate', 'العقارات'], retail_consumer: ['Retail and consumer goods', 'التجزئة والسلع الاستهلاكية'],
  software_technology: ['Software and technology', 'البرمجيات والتكنولوجيا'],
  remote: ['Remote', 'عن بُعد'], hybrid: ['Hybrid', 'هجين'], on_site: ['On-site', 'من مقر العمل'],
  full_time: ['Full-time', 'دوام كامل'], part_time: ['Part-time', 'دوام جزئي'], contract: ['Contract', 'عقد'],
  temporary: ['Temporary', 'مؤقت'], internship: ['Internship', 'تدريب'], freelance: ['Freelance', 'عمل حر'],
  volunteer: ['Volunteer', 'تطوّع'], other: ['Other', 'أخرى'],
  'full-time_and_part-time': ['Full-time or part-time', 'دوام كامل أو جزئي'],
  junior: ['Entry level', 'مبتدئ'], mid: ['Mid-level', 'متوسط الخبرة'], senior: ['Senior', 'ذو خبرة عالية'],
  lead: ['Lead', 'قائد فريق'], manager: ['Manager', 'مدير'],
  yes: ['Yes', 'نعم'], no: ['No', 'لا'], unknown: ['Not specified', 'غير محدّد'],
  year: ['Yearly', 'سنوي'], month: ['Monthly', 'شهري'], hour: ['Hourly', 'بالساعة'],
  hirebase: ['Hirebase', 'Hirebase'], apify_linkedin: ['LinkedIn', 'LinkedIn'],
  apify_google: ['Google Jobs', 'Google Jobs'], apify_linkedin_lebanon: ['LinkedIn Lebanon', 'LinkedIn لبنان'],
};

export function inventoryValueLabel(value, language, key) {
  if (key === 'language') {
    try { return new Intl.DisplayNames([language], { type: 'language' }).of(value); } catch { return value; }
  }
  return VALUES[value]?.[language === 'ar' ? 1 : 0] || value.replace(/_/g, ' ');
}

// Options come from the scoped API inventory. Missing data is never presented
// as a known qualification, salary, or sponsorship promise.
export default function ScoutInventoryFilters({ values, onChange, onSubmit, facets = {}, showLocation = false }) {
  const { language } = useLanguage();
  const copy = LABELS[language] || LABELS.en;
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState({});
  const dialog = useRef(null);
  const id = useId();
  useEffect(() => {
    if (!expanded) return undefined;
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, [expanded]);
  const close = () => { dialog.current?.close(); setExpanded(false); };
  const update = (key, value) => setDraft((previous) => ({ ...previous, [key]: value,
    ...(['currency', 'salary_period'].includes(key) && !value ? { minimum_salary: '' } : {}) }));
  const salaryReady = Boolean(draft.currency && draft.salary_period);
  const count = Object.entries(values).filter(([key, value]) => !['q', 'use_saved_preferences'].includes(key) && value !== '' && value != null && value !== false).length;
  const field = (key) => {
    if (!facets[key]?.length) return null;
    return <label key={key}>{copy[key]}
      {['company', 'skill'].includes(key) ? <>
        <input list={`${id}-${key}`} value={draft[key] || ''} onChange={(e) => update(key, e.target.value)} placeholder={copy.all} maxLength={key === 'company' ? 300 : 150} />
        <datalist id={`${id}-${key}`}>{facets[key].map((value) => <option key={value} value={value} />)}</datalist>
      </> : <select value={draft[key] || ''} onChange={(e) => update(key, e.target.value)}>
        <option value="">{copy.all}</option>
        {facets[key].map((value) => <option key={value} value={value}>{inventoryValueLabel(value, language, key)}</option>)}
      </select>}
    </label>;
  };
  return <>
    <form className="js-inventory-filters" role="search" aria-label={copy.search} onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
      <div className="js-inventory-toolbar">
        <div className="js-inventory-query">
          <label className="js-manual-search__field"><FiSearch aria-hidden="true" />
            <input value={values.q || ''} onChange={(e) => onChange({ ...values, q: e.target.value })} placeholder={copy.q} aria-label={copy.q} maxLength={120} />
          </label>
          <button type="submit" className="js-inventory-search-submit" aria-label={copy.search}><FiSearch aria-hidden="true" /></button>
        </div>
        {showLocation && <label className="js-manual-search__field">
          <input value={values.location || ''} onChange={(e) => onChange({ ...values, location: e.target.value })} placeholder={copy.location} aria-label={copy.location} maxLength={100} />
        </label>}
        <div className="js-scout-tabs js-inventory-actions">
          <button type="button" aria-haspopup="dialog" aria-expanded={expanded} onClick={() => { setDraft({ ...values }); setExpanded(true); }}><FiSliders aria-hidden="true" /> {copy.filters}{count > 0 && <span className="js-filter-count">{count}</span>}</button>
        </div>
      </div>
    </form>
    {expanded && createPortal(<dialog ref={dialog} className="opp-dialog js-inventory-filter-dialog" aria-labelledby={`${id}-title`} dir={language === 'ar' ? 'rtl' : 'ltr'} onCancel={close} onClick={(event) => { if (event.target === dialog.current) close(); }}>
      <form onSubmit={(event) => { event.preventDefault(); onChange(draft); onSubmit(draft); close(); }}>
        <header><h2 id={`${id}-title`}>{copy.filters}</h2><button type="button" className="opp-icon-button" aria-label={copy.close} onClick={close}><FiX /></button></header>
        <div className="js-filter-dialog-body">
          <fieldset><legend>{copy.role}</legend><div className="js-inventory-filter-fields">
            <label>{copy.location}<input value={draft.location || ''} onChange={(e) => update('location', e.target.value)} maxLength={100} /></label>
            {['work_mode', 'job_type', 'company'].map(field)}
          </div></fieldset>
          {['seniority', 'industry', 'skill', 'language'].some((key) => facets[key]?.length) && <fieldset><legend>{copy.experience}</legend><div className="js-inventory-filter-fields">{['seniority', 'industry', 'skill', 'language'].map(field)}</div></fieldset>}
          <fieldset><legend>{copy.pay}</legend><div className="js-inventory-filter-fields">
            {['currency', 'salary_period'].map(field)}
            {facets.salary_period?.length > 0 && <label>{copy.minimum_salary}<input type="number" min="0" max="10000000000" step="any" disabled={!salaryReady} title={!salaryReady ? copy.salaryHint : undefined} value={draft.minimum_salary || ''} onChange={(e) => update('minimum_salary', e.target.value)} />{!salaryReady && <small>{copy.salaryHint}</small>}</label>}
            <label className="js-inventory-checkbox"><input type="checkbox" checked={Boolean(draft.salary_only)} onChange={(e) => update('salary_only', e.target.checked)} />{copy.salary_only}</label>
          </div></fieldset>
          <fieldset><legend>{copy.preferences}</legend><div className="js-inventory-filter-fields">
            {['visa'].map(field)}
            <label>{copy.posted_days}<select value={draft.posted_days || ''} onChange={(e) => update('posted_days', e.target.value)}><option value="">{copy.anytime}</option><option value="1">{copy.day}</option><option value="7">{copy.week}</option><option value="30">{copy.month}</option></select></label>
            <label className="js-inventory-checkbox"><input type="checkbox" checked={Boolean(draft.direct_employer)} onChange={(e) => update('direct_employer', e.target.checked)} />{copy.direct_employer}</label>
          </div></fieldset>
        </div>
        <footer><button type="button" className="opp-button is-secondary" onClick={() => setDraft({ q: values.q || '', location: '' })}>{copy.reset}</button><button type="submit" className="opp-button">{copy.apply}</button></footer>
      </form>
    </dialog>, document.body)}
  </>;
}
